import { useCallback, useEffect, useRef, useState } from 'react';

interface UseIsTruncatedOptions {
  /**
   * When false, the observer pauses and the last measured value is preserved.
   * Useful to keep a "Show Less" button visible while an element is expanded
   * and the line-clamp is removed.
   * @default true
   */
  enabled?: boolean;
}

/**
 * Returns a ref to attach to an element and a boolean indicating whether its
 * content is currently clipped (scrollHeight > clientHeight).
 *
 * Uses ResizeObserver so it reacts to both viewport resizes and content
 * changes without needing window.resize listeners.
 *
 * @example
 * const [ref, isTruncated] = useIsTruncated();
 * // With latch to preserve value while expanded (line-clamp removed):
 * const [ref, isTruncated] = useIsTruncated({ enabled: !isExpanded });
 */
export function useIsTruncated<T extends HTMLElement = HTMLDivElement>(
  options?: UseIsTruncatedOptions,
) {
  const { enabled = true } = options ?? {};
  const [isTruncated, setIsTruncated] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const callbackRef = useCallback(
    (el: T | null) => {
      observerRef.current?.disconnect();

      if (!el || !enabled) return;

      const measure = () => {
        setIsTruncated(el.scrollHeight > el.clientHeight);
      };

      // Measure immediately on mount / when re-enabled
      measure();

      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(el);
    },
    [enabled],
  );

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return [callbackRef, isTruncated] as const;
}
